import { useMemo } from 'react';
import { useAppSelector } from '../store';
import { isSuperAdmin } from '../utils/roleHelper';
import { getReopenedInfo, isStageReopened } from '../utils/stageHelper';

/**
 * Custom hook for handling reopened stages logic
 * Provides candidate data formatted for helper functions and reopened stage information
 */
export const useReopenedStages = () => {
 
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = isSuperAdmin(user);

   const profileData = useAppSelector((state) => isAdmin ? 
    state.superadmin.reviewCandidateData?.profile : state.onboarding.personaldetails);

  // Construct candidate object for helper functions
  const candidateForHelper = useMemo(() => ({
    profile: {
      reopened_stages: profileData?.reopened_stages || null,
      reopened_sections: profileData?.reopened_sections || null,  // Pass raw string, helper will parse it
      reopened_at: profileData?.reopened_at || null,
      review_status: profileData?.review_status || null,
    }
  }), [profileData?.reopened_stages, profileData?.reopened_sections, profileData?.reopened_at, profileData?.review_status]);

  // Get reopened information (parsed stages and sections)
  const reopenedInfo = useMemo(() => {
    return getReopenedInfo(candidateForHelper);
  }, [candidateForHelper]);

  // Helper function to check if a specific stage is reopened
  const isStageReopenedFn = (stageNumber: number) => {
    return isStageReopened(candidateForHelper, stageNumber);
  };

  // Helper function to check if a specific section is reopened
  const isSectionReopenedFn = (sectionKey: string) => {
    const sections = reopenedInfo?.sections || [];
    return sections.includes(sectionKey);
  };

  // Get reopened stage numbers
  const reopenedStageNumbers = useMemo(() => {
    return reopenedInfo?.stages || [];
  }, [reopenedInfo]);

  // Get reopened sections as array
  const reopenedSections = useMemo(() => {
    return reopenedInfo?.sections || [];
  }, [reopenedInfo]);

  return {
    candidateForHelper,
    reopenedInfo,
    reopenedStageNumbers,
    reopenedSections,
    isStageReopenedFn,
    isSectionReopenedFn,
  };
};
